/**
 * Start over: throw a ticket's work away and plan it again.
 *
 * Opened from the ticket's More menu. The reason is needed here: the next
 * attempt's plan and build start from it, so Discard stays off until it has
 * text. (The route still takes none, for older tabs and the Ask dock.) With a pull request open, two boxes say
 * what happens on GitHub: close it (with a comment giving the reason) and
 * delete its branch. Deleting the branch of an open pull request would close
 * it too, so the branch box follows the first one: unchecking "close" unchecks
 * and locks it.
 *
 * Built the way Add people is (`teams/people/AddPeopleDialog.tsx`): the body
 * mounts only while open, so every open starts clean.
 */
import { useState } from "react";

import { Btn } from "../queue/index.js";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog.js";
import {
  START_OVER_CLOSE_PR_OFF,
  START_OVER_CLOSE_PR_ON,
  START_OVER_CONFIRM,
  START_OVER_DELETE_BRANCH,
  START_OVER_DELETE_BRANCH_LOCKED,
  START_OVER_DELETE_BRANCH_ON,
  START_OVER_REASON_LABEL,
  START_OVER_REASON_NEEDED,
  START_OVER_REASON_PLACEHOLDER,
  startOverClosePr,
  startOverEffect,
  startOverTitle,
} from "../work/workCopy.js";
import "./startOver.css";

/** The longest reason the route takes. */
const MAX_REASON = 4_000;

export interface StartOverChoice {
  reason: string;
  closePullRequest: boolean;
  deleteBranch: boolean;
}

export interface StartOverDialogProps {
  open: boolean;
  onClose: () => void;
  ticketKey: string;
  stage: "plan" | "build" | "review";
  /** The open pull request, when there is one: the boxes show only then. */
  prNumber: number | null;
  branchName: string | null;
  hasTracker: boolean;
  /**
   * The reason it opens with: an answer or a note the plan could not take, so
   * Reeve's next plan reads it under Discarded before (spec 2026-11-02).
   */
  initialReason?: string;
  /** Throws with the route's own sentence when the server refuses. */
  onConfirm: (choice: StartOverChoice) => Promise<void>;
}

export function StartOverDialog(props: StartOverDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={(next) => (next ? undefined : props.onClose())}>
      {props.open && <StartOverBody {...props} />}
    </Dialog>
  );
}

function StartOverBody({
  onClose,
  ticketKey,
  stage,
  prNumber,
  branchName,
  hasTracker,
  initialReason,
  onConfirm,
}: StartOverDialogProps) {
  const [reason, setReason] = useState(() => (initialReason ?? "").slice(0, MAX_REASON));
  const [closePullRequest, setClosePullRequest] = useState(true);
  const [deleteBranch, setDeleteBranch] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPr = prNumber != null;
  const hasReason = reason.trim() !== "";
  const branchOn = closePullRequest && deleteBranch;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        reason: reason.trim(),
        closePullRequest: hasPr && closePullRequest,
        deleteBranch: hasPr && branchOn,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not go through.");
      setBusy(false);
    }
  };

  return (
    <DialogContent className="startover" data-testid="start-over-dialog">
      <DialogTitle>{startOverTitle(ticketKey)}</DialogTitle>
      <DialogDescription className="can">{startOverEffect(stage, hasTracker)}</DialogDescription>
      <label className="reasonfield">
        <span className="label">{START_OVER_REASON_LABEL}</span>
        <textarea
          className="wk-gate-input"
          rows={3}
          maxLength={MAX_REASON}
          value={reason}
          placeholder={START_OVER_REASON_PLACEHOLDER}
          aria-label={START_OVER_REASON_LABEL}
          data-testid="start-over-reason"
          onChange={(event) => setReason(event.target.value)}
        />
        <span className="need">{START_OVER_REASON_NEEDED}</span>
      </label>
      {hasPr && (
        <div className="checklist">
          <label>
            <input
              type="checkbox"
              checked={closePullRequest}
              data-testid="start-over-close-pr"
              onChange={(event) => {
                setClosePullRequest(event.target.checked);
                setDeleteBranch(event.target.checked);
              }}
            />
            <span>
              {startOverClosePr(prNumber)}
              <span className="sub">
                {closePullRequest ? START_OVER_CLOSE_PR_ON : START_OVER_CLOSE_PR_OFF}
              </span>
            </span>
          </label>
          <label className={closePullRequest ? undefined : "off"}>
            <input
              type="checkbox"
              checked={branchOn}
              disabled={!closePullRequest}
              data-testid="start-over-delete-branch"
              onChange={(event) => setDeleteBranch(event.target.checked)}
            />
            <span>
              {START_OVER_DELETE_BRANCH} {branchName && <span className="br">{branchName}</span>}
              <span className="sub">
                {closePullRequest ? START_OVER_DELETE_BRANCH_ON : START_OVER_DELETE_BRANCH_LOCKED}
              </span>
            </span>
          </label>
        </div>
      )}
      {error && (
        <p className="err" role="alert">
          {error}
        </p>
      )}
      <div className="verbs">
        <Btn variant="quiet" disabled={busy} onClick={onClose}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          disabled={busy || !hasReason}
          onClick={() => void confirm()}
          data-testid="start-over-confirm"
        >
          {START_OVER_CONFIRM}
        </Btn>
      </div>
    </DialogContent>
  );
}
